import { lazy } from 'react';
import { Badge } from '@mui/material';

import { formatDateTime } from '../../../utils/formatting';
import type { ResourceViewDefinition } from '../types';
import { EncounterHeader } from './encounter-header';
import { encounterRecord } from './encounter-record';
import { statusTone } from './status-tone';

/**
 * Encounter view registration (GDN-2723) — replaces the standalone encounter
 * detail page and the bespoke encounters list.
 *
 * Note the list/detail asymmetry this rides on: the canonical `list` returns
 * flat `Encounter` rows (so these columns read fields directly), while `get`
 * returns an `{encounter, appointment}` composite. The `record` normalizer
 * below tells the shell where the record lives inside that payload, so every
 * slot and the Fields tab see the encounter itself.
 */
export const encounterView: ResourceViewDefinition = {
  label: 'Encounter',
  record: encounterRecord,
  header: EncounterHeader,
  preview: lazy(() =>
    import('./encounter-preview').then(m => ({ default: m.EncounterPreview })),
  ),
  sections: lazy(() =>
    import('./encounter-sections').then(m => ({ default: m.EncounterSections })),
  ),
  // Mirrors the columns the bespoke encounters list showed before the
  // consolidation — Created, Scheduled, Date of Service, Signed and Input Docs
  // were dropped in the move and are restored here (GDN-2734).
  columns: [
    {
      key: 'created_at',
      label: 'Created',
      render: value => (typeof value === 'string' ? formatDateTime(value) : '—'),
    },
    {
      key: 'scheduled_for',
      label: 'Scheduled',
      render: value => (typeof value === 'string' ? formatDateTime(value) : '—'),
    },
    { key: 'date_of_service', label: 'Date of service' },
    {
      key: 'status',
      label: 'Status',
      render: value => (
        <Badge color={statusTone(typeof value === 'string' ? value : null)}>
          {typeof value === 'string' ? value : '—'}
        </Badge>
      ),
    },
    { key: 'encounter_type', label: 'Type' },
    { key: 'note_title', label: 'Note title' },
    { key: 'patient_id', label: 'Patient' },
    {
      key: 'inputs',
      label: 'Input docs',
      render: value => (Array.isArray(value) ? value.length : 0),
    },
    {
      key: 'signed_at',
      label: 'Signed',
      render: value => (typeof value === 'string' ? formatDateTime(value) : '—'),
    },
  ],
  // status / encounter_type are honored because the backend declares them
  // filterable (GDN-2734); the owner dimensions come from the route itself.
  filters: [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'pending', label: 'Pending' },
        { value: 'failed', label: 'Failed' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
    },
    { key: 'encounter_type', label: 'Type', type: 'text' },
    { key: 'patient_id', label: 'Patient id', type: 'text' },
    { key: 'provider_id', label: 'User id', type: 'text' },
  ],
};
